import { create } from "zustand";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { useProjectStore } from "../core_stores/useProjectStore";
import {
  ProjectChatAssistantMessage,
  ProjectChatElement,
} from "./chat_messages/chatModels";
import { useChatMessagesStore } from "./chat_messages/useChatMessagesStore";

interface TechnicalErrorHandlerActions {
  initialize: () => void;
  rollback: (newPrompt: string | null, newImage: File | null) => Promise<void>;
  dispose: () => void;
}

interface TechnicalErrorHandlerState {
  canHandleTechnicalError: boolean;

  actions: TechnicalErrorHandlerActions;
}

const useTechnicalErrorHandlerStore = create<TechnicalErrorHandlerState>(
  (set) => {
    let unsubscribeChatElements: (() => void) | null = null;
    let pendingJobDoneSubscription: (() => void) | null = null;
    let prompt: string | null = null;
    let image: File | null = null;

    function canHandleTechnicalError(
      chatElements: ProjectChatElement[],
    ): boolean {
      if (chatElements.length === 0) {
        return false;
      }
      const lastMessage = chatElements[chatElements.length - 1];
      const isLastMsgTechnicalError =
        lastMessage instanceof ProjectChatAssistantMessage &&
        lastMessage.type === "error" &&
        (lastMessage.value as SharedMessageTypes.ErrorContent).type ===
          SharedMessageTypes.ErrorTypeEnum.enum.technical;
      return isLastMsgTechnicalError && hasLastUserMsgCheckpointId();
    }

    function hasLastUserMsgCheckpointId(): boolean {
      const lastUserMsg = useChatMessagesStore.getState().lastUserMessage;
      return lastUserMsg?.checkpointId !== undefined;
    }

    async function sendMessage() {
      await useProjectStore.getState().actions.sendMessage(prompt!, image);
      await useChatMessagesStore
        .getState()
        .actions.addTemporaryMessage(prompt!, image);
    }

    return {
      canHandleTechnicalError: false,

      actions: {
        initialize: () => {
          unsubscribeChatElements = useChatMessagesStore.subscribe((state) => {
            set({
              canHandleTechnicalError: canHandleTechnicalError(
                state.chatElements.elements ?? [],
              ),
            });
          });
        },

        rollback: async (newPrompt: string | null, newImage: File | null) => {
          prompt = newPrompt;
          image = newImage;
          await useProjectStore.getState().actions.rollback();
          if (prompt) {
            await new Promise<void>((resolve, reject) => {
              // Clean up any existing pending subscription before creating a new one
              if (pendingJobDoneSubscription) {
                pendingJobDoneSubscription();
                pendingJobDoneSubscription = null;
              }

              pendingJobDoneSubscription = useProjectStore.subscribe(
                (state) => state.latestMsg,
                async (latestMsg) => {
                  if (
                    latestMsg?.type ===
                    SharedMessageTypes.MessageTypeEnum.enum.jobDone
                  ) {
                    if (pendingJobDoneSubscription) {
                      pendingJobDoneSubscription();
                      pendingJobDoneSubscription = null;
                    }
                    try {
                      await sendMessage();
                      resolve();
                    } catch (error) {
                      reject(error);
                    }
                  }
                },
              );
            });
          }
          prompt = null;
          image = null;
        },

        dispose: () => {
          if (unsubscribeChatElements) {
            unsubscribeChatElements();
            unsubscribeChatElements = null;
          }
          if (pendingJobDoneSubscription) {
            pendingJobDoneSubscription();
            pendingJobDoneSubscription = null;
          }
          set({ canHandleTechnicalError: false });
          prompt = null;
          image = null;
        },
      },
    };
  },
);

export const useCanHandleTechnicalError = () =>
  useTechnicalErrorHandlerStore((state) => state.canHandleTechnicalError);

export const useTechnicalErrorHandlerActions = () =>
  useTechnicalErrorHandlerStore((state) => state.actions);
